import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Amount } from "@/components/shared/Amount";
import { PageHeader, Panel, EmptyState, fmtDateShort } from "@/components/shared/DashKit";
import { useContent } from "@/context/ContentContext";
import { StatusBadge } from "@/components/shared/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Eye, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TABS = [["all", "Toutes"], ["ongoing", "En cours"], ["completed", "Terminées"], ["refunded", "Remboursées"]];
const GROUP = { ongoing: ["pending", "processing", "in_progress", "partial"], completed: ["completed"], refunded: ["refunded"] };

export default function Orders() {
  const { t } = useContent();
  const [items, setItems] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [f, setF] = useState({ platform_id: "", search: "" });
  const [confirm, setConfirm] = useState(null); // order pending reorder confirmation
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const doReorder = async () => {
    if (!confirm) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", { service_id: confirm.service_id, target: confirm.target, quantity: confirm.quantity });
      toast.success(t("client.toast.reordered", "Commande relancée !"));
      setConfirm(null);
      nav(`/dashboard/orders/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  useEffect(() => { api.get("/platforms").then((r) => setPlatforms(r.data)); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: "200" });
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.append(k, v); });
    const { data } = await api.get(`/orders?${p}`);
    setItems(data.items); setLoading(false);
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v === "all" ? "" : v }));

  const count = (t) => (t === "all" ? items.length : items.filter((o) => GROUP[t].includes(o.status)).length);
  const shown = tab === "all" ? items : items.filter((o) => GROUP[tab].includes(o.status));

  return (
    <div>
      <PageHeader title={t("orders.list.title", "Mes commandes")} subtitle={t("orders.list.subtitle", "Recherchez, filtrez et suivez toutes vos commandes.")} />

      <div className="mb-4 flex flex-wrap gap-2" data-testid="orders-status-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`orders-tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === v ? "border-accent bg-accent text-white" : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
            {t(`orders.tab.${v}`, l)} <span className="ml-1 opacity-70">{count(v)}</span>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-4 lg:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input data-testid="orders-search" value={f.search} onChange={(e) => set("search", e.target.value)} placeholder={t("orders.search.placeholder", "N° de commande ou service...")} className="w-full bg-transparent py-2 text-sm outline-none" />
        </div>
        <Select value={f.platform_id || "all"} onValueChange={(v) => set("platform_id", v)}>
          <SelectTrigger data-testid="orders-platform-filter" className="lg:w-44"><SelectValue placeholder={t("orders.filter.platform", "Plateforme")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("orders.tab.all", "Toutes")}</SelectItem>{platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Panel>
        {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : shown.length === 0 ? <EmptyState title={t("orders.empty.title", "Aucune commande")} subtitle={t("orders.empty.subtitle", "Aucune commande ne correspond à vos filtres.")} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs text-muted-foreground">
                  {[t("client.orders.col.number", "N°"), t("client.orders.col.service", "Service"), t("client.orders.col.target", "Cible"), t("client.orders.col.qty", "Quantité"), t("client.orders.col.price", "Prix"), t("client.orders.col.status", "Statut"), t("client.orders.col.date", "Date"), ""].map((h, i) => <th key={i} className="pb-2 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {shown.map((o) => (
                    <tr key={o.id} className="border-b border-border/60 last:border-0" data-testid={`order-row-${o.order_number}`}>
                      <td className="py-3 font-mono text-xs">#{o.order_number}</td>
                      <td className="py-3 max-w-[180px] truncate">{o.service_name}</td>
                      <td className="py-3 max-w-[140px] truncate text-muted-foreground">{o.target}</td>
                      <td className="py-3">{o.quantity.toLocaleString()}</td>
                      <td className="py-3 font-medium"><Amount value={o.price} /></td>
                      <td className="py-3"><StatusBadge status={o.status} /></td>
                      <td className="py-3 text-muted-foreground">{fmtDateShort(o.created_at)}</td>
                      <td className="py-3"><div className="flex items-center gap-2">
                        <button onClick={() => setConfirm(o)} data-testid={`reorder-${o.order_number}`} title={t("orders.reorder.button", "Recommander")} className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent/25"><RefreshCw className="h-3.5 w-3.5" /> {t("orders.reorder.button", "Recommander")}</button>
                        <Link to={`/dashboard/orders/${o.id}`} data-testid={`view-order-${o.order_number}`} className="inline-flex items-center gap-1 text-accent hover:underline"><Eye className="h-4 w-4" /></Link>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Panel>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && !submitting && setConfirm(null)}>
        <AlertDialogContent data-testid="reorder-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("orders.reorder.confirm_title", "Confirmer la nouvelle commande")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("orders.reorder.confirm_desc", "Le montant sera déduit de votre solde. Vérifiez les détails avant de confirmer.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm && (
            <dl className="space-y-2.5 rounded-xl border border-border bg-secondary/40 p-4 text-sm" data-testid="reorder-details">
              <Row label={t("client.field.service", "Service")} value={confirm.service_name} />
              <Row label={t("client.field.target_link", "Lien cible")} value={<span className="break-all text-right">{confirm.target}</span>} />
              <Row label={t("client.field.quantity", "Quantité")} value={confirm.quantity.toLocaleString()} />
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2.5">
                <dt className="font-medium">{t("client.field.total_price", "Prix total")}</dt>
                <dd className="font-display text-base font-bold text-accent"><Amount value={confirm.price} /></dd>
              </div>
            </dl>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reorder-cancel-btn" disabled={submitting}>{t("client.cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction data-testid="reorder-confirm-btn" onClick={(e) => { e.preventDefault(); doReorder(); }} disabled={submitting}>
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("client.processing", "Traitement...")}</> : t("client.confirm", "Confirmer")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <dt className="shrink-0 text-muted-foreground">{label}</dt>
    <dd className="min-w-0 break-all font-medium sm:max-w-[65%] sm:text-right">{value}</dd>
  </div>
);
